import { Box, HStack, Text } from '@chakra-ui/react';
import React from 'react';

const formatWithCommas = (digits: string): string => {
  if (digits === '') return '';
  return Number(digits).toLocaleString('en-US');
};

type MoneyInputProps = {
  /** Raw, digits-only value (no commas, no currency symbol). */
  rawDigits: string;
  /** Receives the digits-only value after non-numeric characters are stripped. */
  onDigitsChange: (digits: string) => void;
  /** Renders the warning border treatment (e.g. amount matches the current offer). */
  isWarn?: boolean;
  /** When provided, pressing Enter calls this. */
  onEnter?: () => void;
  autoFocus?: boolean;
};

const MoneyInput = ({
  rawDigits,
  onDigitsChange,
  isWarn = false,
  onEnter,
  autoFocus = true,
}: MoneyInputProps) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onDigitsChange(event.target.value.replace(/\D/g, ''));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && onEnter) onEnter();
  };

  return (
    <HStack
      spacing={0}
      borderWidth="1px"
      borderColor={isWarn ? 'orange.300' : 'gainsboro'}
      borderRadius="10px"
      overflow="hidden"
      mb="6px"
      _focusWithin={{ boxShadow: '0 0 0 2px rgba(8, 125, 197, 0.12)' }}
    >
      <Text
        fontFamily="heading"
        fontSize="28px"
        fontWeight="700"
        color="leaseEndBlue"
        px="14px"
        py="12px"
        bg="gray.50"
        borderRightWidth="1px"
        borderRightColor="gainsboro"
        lineHeight="1"
        userSelect="none"
      >
        $
      </Text>
      <Box
        as="input"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={formatWithCommas(rawDigits)}
        onChange={handleChange}
        onKeyDown={onEnter ? handleKeyDown : undefined}
        placeholder="0"
        autoFocus={autoFocus}
        flex="1"
        fontFamily="heading"
        fontSize="28px"
        fontWeight="700"
        color="leaseEndBlue"
        px="14px"
        py="12px"
        bg="white"
        outline="none"
        border="none"
        lineHeight="1"
        sx={{ '&::placeholder': { color: 'gray.500' } }}
      />
    </HStack>
  );
};

export default MoneyInput;
